import React, { useState, useEffect, useRef, useCallback } from "react";
import { apiGet, apiPost, apiDelete, apiPut } from "../../api";
import { t, Locale } from "../../i18n";

// ─── Types ────────────────────────────────────────────────────────────────

interface DnsStatus {
  mx_ok: boolean;
  spf_ok: boolean;
  dkim_ok: boolean;
  all_ok: boolean;
}

interface Propagation {
  status: "pending" | "propagated" | "failed";
  checks_run: number;
  next_check_at: string;
}

interface Domain {
  id: number;
  domain: string;
  dkim_selector: string;
  catch_all_target_email: string | null;
  dns_status: DnsStatus;
  propagation: Propagation | null;
  last_checked_at: string | null;
  activated_at: string | null;
  created_at: string;
}

interface VerifyOwnershipResponse {
  token: string;
  txt_record: string;
  instructions: string;
}

interface DnsSetupInfo {
  type: string;
  name: string;
  value: string;
  priority?: number;
}

interface ConfirmOwnershipResponse {
  domain: string;
  verified: true;
  dkim_selector: string;
  dkim_public_key: string;
  rspamd_reloaded: boolean;
  dns_setup_required: {
    mx: DnsSetupInfo;
    spf: DnsSetupInfo;
    dkim: DnsSetupInfo;
  };
}

type AddDomainState = "idle" | "verify" | "confirm" | "setup";

// ─── CatchAllEditor Component ──────────────────────────────────────────────

interface CatchAllEditorProps {
  domain: Domain;
  onSaved: () => void;
  locale: Locale;
}

function CatchAllEditor({ domain, onSaved, locale }: CatchAllEditorProps) {
  const [enabled, setEnabled] = useState(domain.catch_all_target_email !== null);
  const [inputValue, setInputValue] = useState(domain.catch_all_target_email ?? "");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close suggestion list on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (q.length < 1) {
      setSuggestions([]);
      return;
    }
    const result = await apiGet<{ mailboxes: { email: string }[] }>(
      `/v1/mailboxes/search?q=${encodeURIComponent(q)}`
    );
    if (result.ok) {
      setSuggestions(result.data.mailboxes.map((m) => m.email));
    }
  }, []);

  function handleInputChange(value: string) {
    setInputValue(value);
    setError(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
      setShowSuggestions(true);
    }, 200);
  }

  function handlePickSuggestion(email: string) {
    setInputValue(email);
    setSuggestions([]);
    setShowSuggestions(false);
  }

  async function handleSave() {
    if (enabled && !inputValue.trim()) {
      setError(t(locale, "domains.catchAll.error.required"));
      return;
    }
    setSaving(true);
    setError(null);

    const mailbox_email = enabled ? inputValue.trim().toLowerCase() : null;
    const result = await apiPut<{ ok: boolean }>(
      `/v1/domains/${domain.id}/catch-all`,
      { mailbox_email }
    );

    setSaving(false);
    if (result.ok) {
      setShowSuggestions(false);
      onSaved();
    } else {
      if (result.status === 404) {
        setError(t(locale, "domains.catchAll.error.notFound"));
      } else if ((result.error ?? "").includes("forwarding")) {
        setError(t(locale, "domains.catchAll.error.forwarding"));
      } else {
        setError(result.error ?? t(locale, "common.saveFailed"));
      }
    }
  }

  // Determine if the form has unsaved changes
  const currentTarget = domain.catch_all_target_email;
  const isDirty = enabled !== (currentTarget !== null) ||
    (enabled && inputValue.toLowerCase() !== (currentTarget ?? "").toLowerCase());

  return (
    <div
      style={{
        borderTop: "1px solid var(--line)",
        marginTop: "12px",
        paddingTop: "12px"
      }}
    >
      <div
        style={{
          fontSize: "12px",
          fontWeight: 600,
          marginBottom: "8px",
          color: "var(--text)"
        }}
      >
        {t(locale, "domains.catchAll")}
      </div>

      {/* Enable / disable toggle row */}
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "12px",
          color: "var(--muted)",
          cursor: "pointer",
          marginBottom: "8px"
        }}
      >
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            setEnabled(e.target.checked);
            setError(null);
            if (!e.target.checked) {
              setInputValue("");
              setSuggestions([]);
            }
          }}
        />
        {t(locale, "domains.catchAll.description")}
      </label>

      {enabled && (
        <div ref={wrapperRef} style={{ position: "relative", marginBottom: "8px" }}>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) setShowSuggestions(true);
            }}
            placeholder="user@yourdomain.com"
            style={{
              width: "100%",
              padding: "7px 8px",
              border: "1px solid var(--control-border)",
              borderRadius: "4px",
              fontSize: "12px",
              boxSizing: "border-box",
              background: "var(--bg)",
              color: "var(--text)"
            }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                background: "var(--canvas)",
                border: "1px solid var(--line)",
                borderRadius: "4px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                zIndex: 100,
                marginTop: "2px"
              }}
            >
              {suggestions.map((email) => (
                <div
                  key={email}
                  onMouseDown={() => handlePickSuggestion(email)}
                  style={{
                    padding: "8px 10px",
                    fontSize: "12px",
                    cursor: "pointer",
                    color: "var(--text)"
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = "var(--hover)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = "transparent";
                  }}
                >
                  {email}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {enabled && (
        <p style={{ fontSize: "11px", color: "var(--muted)", margin: "0 0 8px" }}>
          {t(locale, "domains.catchAll.note")}
        </p>
      )}

      {error && (
        <p style={{ fontSize: "11px", color: "#ef4444", margin: "0 0 8px" }}>
          {error}
        </p>
      )}

      {isDirty && (
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: "5px 12px",
            fontSize: "12px",
            background: "var(--primary)",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.6 : 1
          }}
        >
          {saving ? t(locale, "common.saving") : t(locale, "common.save")}
        </button>
      )}
    </div>
  );
}

// ─── DomainSettings Component ──────────────────────────────────────────────

export function DomainSettings(props: { locale: Locale }) {
  const locale = props.locale;
  // Domain list state
  const [domains, setDomains] = useState<Domain[]>([]);
  const [domainsLoading, setDomainsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add domain flow state
  const [addState, setAddState] = useState<AddDomainState>("idle");
  const [newDomain, setNewDomain] = useState("");
  const [verifyToken, setVerifyToken] = useState<string | null>(null);
  const [txtRecord, setTxtRecord] = useState<string | null>(null);
  const [dnsSetup, setDnsSetup] = useState<ConfirmOwnershipResponse | null>(null);
  const [dnsProvider, setDnsProvider] = useState("other");
  const [addLoading, setAddLoading] = useState(false);

  // Polling control
  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load domains on mount
  useEffect(() => {
    fetchDomains();
  }, []);

  // Poll DNS status every 30 seconds
  useEffect(() => {
    pollIntervalRef.current = setInterval(() => {
      fetchDomains();
    }, 30000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  async function fetchDomains() {
    const result = await apiGet<{ domains: Domain[] }>("/v1/domains");
    if (result.ok) {
      setDomains(result.data.domains);
    } else {
      setError(result.error);
    }
    setDomainsLoading(false);
  }

  async function handleAddDomain() {
    if (!newDomain.trim()) {
      setError(t(locale, "domains.error.required"));
      return;
    }

    setAddLoading(true);
    setError(null);

    const result = await apiPost<VerifyOwnershipResponse>("/v1/domains/verify-ownership", {
      domain: newDomain.toLowerCase()
    });
    setAddLoading(false);

    if (result.ok) {
      setVerifyToken(result.data.token);
      setTxtRecord(result.data.txt_record);
      setAddState("verify");
    } else {
      setError(result.error);
    }
  }

  async function handleConfirmOwnership() {
    setAddLoading(true);
    setError(null);

    const result = await apiPost<ConfirmOwnershipResponse>("/v1/domains/confirm-ownership", {
      domain: newDomain.toLowerCase()
    });
    setAddLoading(false);

    if (result.ok) {
      setDnsSetup(result.data);
      setAddState("setup");
      // Refresh domain list
      await fetchDomains();
    } else {
      setError(result.error);
    }
  }

  async function handleDeleteDomain(domain: string) {
    if (!confirm(`Delete ${domain}? This cannot be undone.`)) return;

    setError(null);
    const result = await apiDelete("/v1/domains/" + encodeURIComponent(domain.toLowerCase()), {});

    if (result.ok) {
      await fetchDomains();
    } else {
      setError(result.error);
    }
  }

  async function handleCheckDns(domain: string) {
    setError(null);
    const result = await apiPost("/v1/domains/" + encodeURIComponent(domain.toLowerCase()) + "/check-dns", {});

    if (result.ok) {
      await fetchDomains();
    } else {
      setError(result.error);
    }
  }

  function resetAddFlow() {
    setAddState("idle");
    setNewDomain("");
    setVerifyToken(null);
    setTxtRecord(null);
    setDnsSetup(null);
    setDnsProvider("other");
  }

  // ─── Render helpers ───────────────────────────────────────────────────────

  function getDnsInstructions() {
    if (!dnsSetup) return null;

    const instructions: Record<string, Record<string, string>> = {
      cloudflare: {
        mx: "Type: MX | Name: @ | Content: mx1.irondew.com | Priority: 10",
        spf: "Type: TXT | Name: @ | Content: v=spf1 include:kveja.com ~all",
        dkim: `Type: TXT | Name: ${dnsSetup.dkim_selector}._domainkey | Content: v=DKIM1; k=rsa; p=${dnsSetup.dkim_public_key}`
      },
      godaddy: {
        mx: `Host: @ | Type: MX | Value: mx1.irondew.com | Priority: 10`,
        spf: `Host: @ | Type: TXT | Value: "v=spf1 include:kveja.com ~all"`,
        dkim: `Host: ${dnsSetup.dkim_selector}._domainkey | Type: TXT | Value: "v=DKIM1; k=rsa; p=${dnsSetup.dkim_public_key}"`
      },
      namecheap: {
        mx: `Type: MX | Host: @ | Value: mx1.irondew.com | Priority: 10`,
        spf: `Type: TXT Record | Host: @ | Value: v=spf1 include:kveja.com ~all`,
        dkim: `Type: TXT Record | Host: ${dnsSetup.dkim_selector}._domainkey | Value: v=DKIM1; k=rsa; p=${dnsSetup.dkim_public_key}`
      },
      route53: {
        mx: `Type: MX | Name: ${dnsSetup.dkim_selector}._domainkey.${dnsSetup.dkim_selector} | Value: 10 mx1.irondew.com`,
        spf: `Type: TXT | Name: ${dnsSetup.dkim_selector}._domainkey.${dnsSetup.dkim_selector} | Value: "v=spf1 include:kveja.com ~all"`,
        dkim: `Type: TXT | Name: ${dnsSetup.dkim_selector}._domainkey.${dnsSetup.dkim_selector} | Value: "v=DKIM1; k=rsa; p=${dnsSetup.dkim_public_key}"`
      }
    };

    return instructions[dnsProvider] || instructions.other;
  }

  function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString();
  }

  if (addState === "idle" && domainsLoading) {
    return <div style={{ padding: "20px" }}>{t(locale, "domains.loading")}</div>;
  }

  return (
    <div style={{ maxWidth: "800px" }}>
      <h2 style={{ marginTop: 0, marginBottom: "4px" }}>{t(locale, "domains.title")}</h2>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: "24px", fontSize: "13px" }}>
        {t(locale, "domains.description")}
      </p>

      {error && (
        <div
          style={{
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            borderRadius: "6px",
            padding: "12px",
            marginBottom: "16px",
            color: "#ef4444",
            fontSize: "13px"
          }}
        >
          {error}
        </div>
      )}

      {addState === "idle" && (
        <div>
          {/* Domain List */}
          {domains.length > 0 ? (
            <div style={{ marginBottom: "24px" }}>
              <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "12px" }}>{t(locale, "domains.yourDomains")}</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {domains.map((domain) => (
                  <div
                    key={domain.id}
                    style={{
                      background: "var(--canvas)",
                      border: "1px solid var(--line)",
                      borderRadius: "8px",
                      padding: "16px"
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start"
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "8px" }}>
                          {domain.domain}
                        </div>

                        {/* DNS Status Indicators */}
                        <div style={{ display: "flex", gap: "16px", marginBottom: "8px", fontSize: "12px" }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              color: domain.dns_status.mx_ok ? "#22c55e" : "#9ca3af"
                            }}
                          >
                            <span>●</span> MX
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              color: domain.dns_status.spf_ok ? "#22c55e" : "#9ca3af"
                            }}
                          >
                            <span>●</span> SPF
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              color: domain.dns_status.dkim_ok ? "#22c55e" : "#9ca3af"
                            }}
                          >
                            <span>●</span> DKIM
                          </div>
                        </div>

                        {/* Activation and Check Status */}
                        <div style={{ fontSize: "12px", color: "var(--muted)" }}>
                          {domain.activated_at ? (
                            <>Activated {formatDate(domain.activated_at)}</>
                          ) : domain.propagation?.status === "pending" ? (
                            <>
                              Checking DNS ({domain.propagation.checks_run} checks){" "}
                              {domain.propagation.next_check_at && (
                                <>• Next check: {new Date(domain.propagation.next_check_at).toLocaleTimeString()}</>
                              )}
                            </>
                          ) : (
                            <>Added {formatDate(domain.created_at)}</>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: "flex", gap: "8px", flexDirection: "column" }}>
                        {domain.propagation?.status === "pending" && (
                          <button
                            onClick={() => handleCheckDns(domain.domain)}
                            style={{
                              padding: "6px 12px",
                              fontSize: "12px",
                              background: "transparent",
                              border: "1px solid var(--control-border)",
                              borderRadius: "4px",
                              cursor: "pointer",
                              color: "var(--text)"
                            }}
                          >
                            {t(locale, "domains.checkNow")}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteDomain(domain.domain)}
                          style={{
                            padding: "6px 12px",
                            fontSize: "12px",
                            background: "transparent",
                            border: "1px solid var(--control-border)",
                            borderRadius: "4px",
                            cursor: "pointer",
                            color: "var(--danger)"
                          }}
                        >
                          {t(locale, "common.remove")}
                        </button>
                      </div>
                    </div>

                    {/* Catch-all section — only for activated domains */}
                    {domain.activated_at && (
                      <CatchAllEditor domain={domain} onSaved={fetchDomains} locale={locale} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Add Domain Button */}
          <button
            onClick={() => setAddState("verify")}
            style={{
              width: "100%",
              padding: "12px",
              background: "var(--primary)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: 600
            }}
          >
            {t(locale, "domains.add")}
          </button>
        </div>
      )}

      {addState === "verify" && (
        <div>
          <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "12px" }}>{t(locale, "domains.step1.title")}</h3>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>
              {t(locale, "domains.step1.domainName")}
            </label>
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                fontSize: "13px",
                boxSizing: "border-box"
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleAddDomain}
              disabled={addLoading || !newDomain.trim()}
              style={{
                flex: 1,
                padding: "8px",
                background: "var(--primary)",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: addLoading || !newDomain.trim() ? "not-allowed" : "pointer",
                fontSize: "13px",
                opacity: addLoading || !newDomain.trim() ? 0.6 : 1
              }}
            >
              {addLoading ? t(locale, "domains.step1.generatingToken") : t(locale, "common.next")}
            </button>
            <button
              onClick={resetAddFlow}
              disabled={addLoading}
              style={{
                flex: 1,
                padding: "8px",
                background: "transparent",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "13px"
              }}
            >
              {t(locale, "common.cancel")}
            </button>
          </div>
        </div>
      )}

      {addState === "verify" && verifyToken && (
        <div style={{ marginTop: "20px" }}>
          <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "12px" }}>{t(locale, "domains.step2.title")}</h3>

          <p style={{ color: "var(--muted)", fontSize: "12px", marginBottom: "12px" }}>
            {t(locale, "domains.step2.txtInstruction")}
          </p>

          <div
            style={{
              background: "var(--bg)",
              border: "1px solid var(--line)",
              borderRadius: "6px",
              padding: "12px",
              marginBottom: "12px",
              fontFamily: "monospace",
              fontSize: "12px",
              wordBreak: "break-all"
            }}
          >
            {txtRecord}
          </div>

          <p style={{ color: "var(--muted)", fontSize: "12px", marginBottom: "12px" }}>
            {t(locale, "domains.step2.txtDetails")}
          </p>

          <p style={{ color: "var(--muted)", fontSize: "12px", marginBottom: "16px" }}>
            {t(locale, "domains.step2.propagationNote")}
          </p>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleConfirmOwnership}
              disabled={addLoading}
              style={{
                flex: 1,
                padding: "8px",
                background: "var(--primary)",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: addLoading ? "not-allowed" : "pointer",
                fontSize: "13px",
                opacity: addLoading ? 0.6 : 1
              }}
            >
              {addLoading ? t(locale, "common.verifying") : t(locale, "domains.step2.verifyBtn")}
            </button>
            <button
              onClick={resetAddFlow}
              disabled={addLoading}
              style={{
                flex: 1,
                padding: "8px",
                background: "transparent",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "13px"
              }}
            >
              {t(locale, "common.cancel")}
            </button>
          </div>
        </div>
      )}

      {addState === "setup" && dnsSetup && (
        <div>
          <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "12px" }}>{t(locale, "domains.step3.title")}</h3>

          <p style={{ color: "var(--muted)", fontSize: "12px", marginBottom: "12px" }}>
            {t(locale, "domains.step3.description")}
          </p>

          {/* Provider Selector */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>
              {t(locale, "domains.step3.providerLabel")}
            </label>
            <select
              value={dnsProvider}
              onChange={(e) => setDnsProvider(e.target.value)}
              style={{
                width: "100%",
                padding: "6px",
                border: "1px solid var(--control-border)",
                borderRadius: "4px",
                fontSize: "12px"
              }}
            >
              <option value="other">Generic (most providers)</option>
              <option value="cloudflare">Cloudflare</option>
              <option value="godaddy">GoDaddy</option>
              <option value="namecheap">Namecheap</option>
              <option value="route53">AWS Route 53</option>
            </select>
          </div>

          {/* DNS Records */}
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginBottom: "16px" }}>
            {["MX", "SPF", "DKIM"].map((recordType) => {
              const setup =
                recordType === "MX"
                  ? dnsSetup.dns_setup_required.mx
                  : recordType === "SPF"
                    ? dnsSetup.dns_setup_required.spf
                    : dnsSetup.dns_setup_required.dkim;
              const instructions = getDnsInstructions();
              const instructionKey =
                recordType === "MX" ? "mx" : recordType === "SPF" ? "spf" : "dkim";

              return (
                <div
                  key={recordType}
                  style={{
                    background: "var(--canvas)",
                    border: "1px solid var(--line)",
                    borderRadius: "6px",
                    padding: "12px"
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "8px" }}>
                    {recordType} Record
                  </div>
                  {instructions && (
                    <div
                      style={{
                        background: "var(--bg)",
                        border: "1px solid var(--line)",
                        borderRadius: "4px",
                        padding: "8px",
                        fontSize: "11px",
                        fontFamily: "monospace",
                        marginBottom: "8px",
                        color: "var(--muted)",
                        lineHeight: "1.5"
                      }}
                    >
                      {instructions[instructionKey]}
                    </div>
                  )}
                  <details style={{ cursor: "pointer", fontSize: "11px", color: "var(--muted)" }}>
                    <summary>{t(locale, "domains.step3.fullDetails")}</summary>
                    <div
                      style={{
                        marginTop: "8px",
                        background: "var(--bg)",
                        padding: "8px",
                        borderRadius: "4px",
                        fontFamily: "monospace",
                        fontSize: "11px",
                        wordBreak: "break-all",
                        lineHeight: "1.6"
                      }}
                    >
                      <div>Type: {setup.type}</div>
                      <div>Name: {setup.name}</div>
                      {setup.priority && <div>Priority: {setup.priority}</div>}
                      <div>Value: {setup.value}</div>
                    </div>
                  </details>
                </div>
              );
            })}
          </div>

          <p style={{ color: "var(--muted)", fontSize: "12px", marginBottom: "16px" }}>
            {t(locale, "domains.step3.propagationNote")}
          </p>

          <button
            onClick={() => {
              resetAddFlow();
              fetchDomains();
            }}
            style={{
              width: "100%",
              padding: "8px",
              background: "var(--primary)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px"
            }}
          >
            {t(locale, "common.done")}
          </button>
        </div>
      )}
    </div>
  );
}
